import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Camera, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import Button from "../common/Button";
import Input from "../common/Input";
import OtpVerifyPanel from "./OtpVerifyPanel";
import AddressAutocomplete from "./AddressAutocomplete";
import { getMyProfile, addBusinessInfo, uploadSingleImage, uploadBusinessDocument } from "../../services/accountService";
import { color, radius } from "../../styles/theme";

const normalizePhone = (phone) => `${phone ?? ""}`.replace(/\s+/g, "");

const BUSINESS_TYPES = ["Promoter", "Entertainer", "Artist", "Performer", "Influencer", "Self Entrepreneur", "Photographer", "others"];

/**
 * Business details — ported from mobile's add-business-detail screen
 * (src/Screens/add-business-detail). Promoter accounts only (gated by the
 * parent AccountSettings page). Address uses the lighter Places Autocomplete
 * field (AddressAutocomplete) instead of mobile's full pin-drop map — see
 * the approved plan for why.
 */
const BusinessDetailsTab = () => {
  const { t } = useTranslation(["settings", "common"]);
  const { user, updateUser } = useAuth();
  const business = user?.BusinessDetails || {};

  const [businessName, setBusinessName] = useState(business.BusinessName || "");
  const [businessNickName, setBusinessNickName] = useState(business.BusinessNickName || "");
  const [businessType, setBusinessType] = useState(business.BusinessType || "");
  const [contactEmail, setContactEmail] = useState(business.ContactEmailAddress || user?.Email || "");
  const [registrationNumber, setRegistrationNumber] = useState(business.BusinessRegistrationNumber || "");
  const [registrationCourt, setRegistrationCourt] = useState(business.RegistrationCourt || "");
  const [phone, setPhone] = useState(business.PhoneNumber || "");
  const [website, setWebsite] = useState(business.Website || "");
  const [imprint, setImprint] = useState(business.CompanyImprint || "");
  const [logo, setLogo] = useState(business.LogoUrl || "");
  const [logoUploading, setLogoUploading] = useState(false);

  const [address, setAddress] = useState({
    address: business.StreetAddress || "",
    city: business.City || "",
    country: business.Country || "",
    postal_code: business.PostalCode || "",
    latitude: user?.Latitude,
    longitude: user?.Longitude,
  });

  const [companyDocUrl, setCompanyDocUrl] = useState(business.CompanyRegistrationDocumentUrl || "");
  const [ownerDocUrl, setOwnerDocUrl] = useState(business.OwnerIdentityDocumentUrl || "");
  const [uploadingDoc, setUploadingDoc] = useState(null); // 'company' | 'owner' | null

  const [saving, setSaving] = useState(false);
  const [otpPending, setOtpPending] = useState(null);

  const refreshProfile = async () => {
    const res = await getMyProfile();
    const fresh = res.data?.Result || res.data;
    if (fresh) updateUser(fresh);
    return fresh;
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const url = await uploadSingleImage(file);
      if (url) setLogo(url);
      else toast.error(t("businessDetailsTab.toasts.logoUploadFailed"));
    } catch {
      toast.error(t("businessDetailsTab.toasts.logoUploadFailed"));
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const handleDocPick = (kind) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(kind);
    try {
      const folderName = kind === "company" ? "CompanyDocument" : "OwnerDocument";
      const url = await uploadBusinessDocument(file, folderName);
      if (url) {
        if (kind === "company") setCompanyDocUrl(url);
        else setOwnerDocUrl(url);
      } else {
        toast.error(t("businessDetailsTab.toasts.documentUploadFailed"));
      }
    } catch {
      toast.error(t("businessDetailsTab.toasts.documentUploadFailed"));
    } finally {
      setUploadingDoc(null);
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    if (!businessName.trim() || !businessNickName.trim() || !businessType) {
      toast.error(t("businessDetailsTab.toasts.nameNicknameTypeRequired"));
      return;
    }
    if (!address.address || !address.country) {
      toast.error(t("businessDetailsTab.toasts.addressCountryRequired"));
      return;
    }
    if (!companyDocUrl) {
      toast.error(t("businessDetailsTab.toasts.companyDocRequired"));
      return;
    }

    setSaving(true);
    try {
      const cleanedNewPhone = normalizePhone(phone);
      const cleanedOldPhone = normalizePhone(business.PhoneNumber);

      const payload = {
        BusinessNickName: businessNickName,
        BusinessName: businessName,
        BusinessType: businessType,
        BusinessRegistrationNumber: registrationNumber,
        PhoneNumber: cleanedNewPhone,
        CompanyImprint: imprint,
        Website: website || null,
        RegistrationCourt: registrationCourt,
        Email: user?.Email,
        ContactEmailAddress: contactEmail,
        StreetAddress: address.address,
        City: address.city,
        State: "",
        PostalCode: address.postal_code || "",
        Country: address.country,
        Latitude: address.latitude ?? "",
        Longitude: address.longitude ?? "",
        LogoUrl: logo,
        CompanyRegistrationDocumentUrl: companyDocUrl,
        OwnerIdentityDocumentUrl: ownerDocUrl || null,
      };

      if (cleanedNewPhone === cleanedOldPhone) delete payload.PhoneNumber;

      const res = await addBusinessInfo(payload);

      const isPhoneVerificationRequired = cleanedNewPhone !== cleanedOldPhone || user?.PhoneNumberConfirmed === false;
      const canVerifyPhoneNow = user?.EmailConfirmed && business?.IsVerified;

      if (isPhoneVerificationRequired && canVerifyPhoneNow && cleanedNewPhone) {
        setOtpPending({ phone: cleanedNewPhone });
        toast.success(res.data?.Message || t("businessDetailsTab.toasts.updatedVerifyPhone"));
        return;
      }

      toast.success(res.data?.Message || t("businessDetailsTab.toasts.updatedSuccess"));
      await refreshProfile();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("businessDetailsTab.toasts.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (otpPending) {
    return (
      <OtpVerifyPanel
        userId={user?.UserId}
        email={user?.Email}
        phoneNumber={otpPending.phone}
        onVerified={async () => {
          setOtpPending(null);
          await refreshProfile();
        }}
        onCancel={() => setOtpPending(null)}
      />
    );
  }

  return (
    <div style={s.wrap}>
      <div style={s.avatarRow}>
        <div style={s.logoBox}>
          {logo ? <img src={logo} alt="" style={s.logoImg} /> : <span style={s.logoInitial}>{(businessNickName || "B").charAt(0).toUpperCase()}</span>}
        </div>
        <label className="btn btn-secondary btn-sm" style={s.avatarBtn}>
          <Camera size={13} strokeWidth={2.25} /> {logoUploading ? t("businessDetailsTab.uploading") : t("businessDetailsTab.changeLogo")}
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleLogoPick} disabled={logoUploading} />
        </label>
      </div>

      <Input label={t("businessDetailsTab.businessNameLabel")} value={businessName} onChange={(e) => setBusinessName(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ""))} />
      <Input label={t("businessDetailsTab.businessNicknameLabel")} value={businessNickName} onChange={(e) => setBusinessNickName(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ""))} />

      <div style={{ marginBottom: 16 }}>
        <label style={s.fieldLabel}>{t("businessDetailsTab.businessTypeLabel")}</label>
        <select className="input" value={businessType} onChange={(e) => setBusinessType(e.target.value)}>
          <option value="">{t("businessDetailsTab.selectOption")}</option>
          {BUSINESS_TYPES.map((bt) => <option key={bt} value={bt}>{bt}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={s.fieldLabel}>{t("businessDetailsTab.accountEmailLabel")}</label>
        <div style={s.readonlyRow}><span style={s.readonlyValue}>{user?.Email}</span></div>
      </div>

      <Input label={t("businessDetailsTab.contactEmailLabel")} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
      <Input label={t("businessDetailsTab.registrationNumberLabel")} value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
      <Input label={t("businessDetailsTab.registrationCourtLabel")} value={registrationCourt} onChange={(e) => setRegistrationCourt(e.target.value)} />
      <Input label={t("businessDetailsTab.phoneNumberLabel")} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+49 151 12345678" />
      <Input label={t("businessDetailsTab.websiteLabel")} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" />

      <div style={{ marginBottom: 16 }}>
        <label style={s.fieldLabel}>{t("businessDetailsTab.businessAddressLabel")}</label>
        <AddressAutocomplete value={address.address} onSelect={(place) => setAddress(place)} placeholder={t("businessDetailsTab.businessAddressPlaceholder")} />
        {address.address && (
          <p style={s.addressPreview}>
            {address.address}{address.city ? `, ${address.city}` : ""}{address.country ? `, ${address.country}` : ""}
          </p>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={s.fieldLabel}>{t("businessDetailsTab.companyImprintLabel")}</label>
        <textarea className="input" style={s.textarea} rows={4} value={imprint} onChange={(e) => setImprint(e.target.value)} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={s.fieldLabel}>{t("businessDetailsTab.companyDocumentLabel")}</label>
        {companyDocUrl && <p style={s.docLink}><a href={companyDocUrl} target="_blank" rel="noreferrer" style={s.docLinkAnchor}><ExternalLink size={11} strokeWidth={2.25} /> {t("businessDetailsTab.viewCurrentDocument")}</a></p>}
        <label className="btn btn-secondary btn-sm" style={s.uploadBtn}>
          {uploadingDoc === "company" ? t("businessDetailsTab.uploading") : companyDocUrl ? t("businessDetailsTab.replaceDocument") : t("businessDetailsTab.uploadDocument")}
          <input type="file" accept="image/*,.pdf" style={{ display: "none" }} onChange={handleDocPick("company")} disabled={uploadingDoc === "company"} />
        </label>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={s.fieldLabel}>{t("businessDetailsTab.ownerDocumentLabel")}</label>
        {ownerDocUrl && <p style={s.docLink}><a href={ownerDocUrl} target="_blank" rel="noreferrer" style={s.docLinkAnchor}><ExternalLink size={11} strokeWidth={2.25} /> {t("businessDetailsTab.viewCurrentDocument")}</a></p>}
        <label className="btn btn-secondary btn-sm" style={s.uploadBtn}>
          {uploadingDoc === "owner" ? t("businessDetailsTab.uploading") : ownerDocUrl ? t("businessDetailsTab.replaceDocument") : t("businessDetailsTab.uploadDocument")}
          <input type="file" accept="image/*,.pdf" style={{ display: "none" }} onChange={handleDocPick("owner")} disabled={uploadingDoc === "owner"} />
        </label>
      </div>

      <Button onClick={handleSave} disabled={saving} style={{ marginTop: 8 }}>
        {saving ? t("businessDetailsTab.saving") : t("businessDetailsTab.saveButton")}
      </Button>
    </div>
  );
};

const s = {
  wrap: { maxWidth: 480 },
  avatarRow: { display: "flex", alignItems: "center", gap: 16, marginBottom: 26 },
  logoBox: {
    width: 72, height: 72, borderRadius: radius.md, background: color.primary500,
    display: "flex", alignItems: "center", justifyContent: "center",
    overflow: "hidden", flexShrink: 0,
  },
  logoImg: { width: "100%", height: "100%", objectFit: "cover" },
  logoInitial: { color: "#fff", fontSize: 26, fontWeight: 700 },
  avatarBtn: { display: "inline-flex" },
  fieldLabel: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  textarea: { resize: "vertical", fontFamily: "inherit" },
  readonlyRow: {
    padding: "10px 13px", background: color.surface, border: `1.5px solid ${color.border}`,
    borderRadius: radius.md,
  },
  readonlyValue: { fontSize: 13.5, color: color.gray900 },
  addressPreview: { fontSize: 12, color: color.textSecondary, marginTop: 6 },
  docLink: { fontSize: 12, marginBottom: 8 },
  docLinkAnchor: { display: "inline-flex", alignItems: "center", gap: 4, color: color.primary600, textDecoration: "none" },
  uploadBtn: { display: "inline-flex" },
};

export default BusinessDetailsTab;
